/**
 * Branch Teams — listing with light hero banner + dark table head.
 *
 * Palette (no purple):
 *   #fc5d1b  orange  — CTA, accents, expanded row marker
 *   #0F172A  slate   — table head, chips
 *   #181436  navy    — small buttons
 *   #F1F5F9  canvas  — page background
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { getTeamsByBranchAPI } from '../../context-api/api-services/GetTeamsByBranch.service';
import {
  formatTeamsErrors,
  formatCell,
  getInitials,
  isVerifiedStatus,
  avatarInitialSx,
  resolveBranchNameForQuery,
  getTeamInfo,
  getTeamRowKey,
  isAccountActive,
  isAccountPending,
} from './branchTeamsUtils';
import { AccountStatusChip, TeamRowDetailsPanel } from './BranchTeamsShared';
import { AddMemberModal } from './BranchTeamsAddModal';
import { EditMemberModal } from './BranchTeamsEditModal';
import { isMobile, isIpad } from '../../utils/ScreenSizeHelper';

/* ── design tokens ── */
const C = {
  orange: '#fc5d1b',
  navy: '#181436',
  dark: '#0F172A',
  pageBg: '#F1F5F9',
  cardBg: '#FFFFFF',
  border: '#E2E8F0',
  headBg: '#F8FAFC',
  rowHover: 'rgba(252,93,27,0.04)',
  rowExpanded: 'rgba(252,93,27,0.07)',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  shadow: '0 1px 3px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.08)',
  shadowOrange: '0 8px 28px rgba(252,93,27,0.32)',
};

const TABLE_COLUMNS = [
  'Member',
  'Team User ID',
  'Phone Verified',
  'User Type',
  'Account Status',
  '',
];

const TABLE_COL_COUNT = TABLE_COLUMNS.length;

/** No horizontal table scroll below this width — columns shrink + ellipsis instead. */
const VIEWPORT_W = typeof window !== 'undefined' ? window.screen.availWidth : 1200;
const isNarrowTable = VIEWPORT_W < 786;

function tableHeadCellSx(extra = {}) {
  return {
    fontFamily: 'Inter Bold',
    fontSize: '0.63rem',
    color: 'rgba(255,255,255,0.5)',
    bgcolor: C.dark,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    borderBottom: `2px solid ${C.orange}40`,
    whiteSpace: 'nowrap',
    py: 1.75,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...extra,
  };
}

const TABLE_COL_SX = {
  member: {
    width: isNarrowTable ? '22%' : isIpad ? '24%' : '26%',
    maxWidth: isNarrowTable ? 128 : isIpad ? 168 : 260,
    overflow: 'hidden',
  },
  teamId: {
    width: isNarrowTable ? '22%' : '20%',
    maxWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  phone: {
    width: isNarrowTable ? '11%' : '12%',
    whiteSpace: 'nowrap',
  },
  userType: {
    width: isNarrowTable ? '16%' : '15%',
    overflow: 'hidden',
  },
  status: {
    width: isNarrowTable ? '17%' : '16%',
    overflow: 'hidden',
  },
  actions: {
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    px: 0.5,
  },
};

/** Phone verification status as icon + short label (phone in tooltip). */
function PhoneVerifiedCell({ verified, phone, compact = false }) {
  const phoneLabel = phone != null && String(phone).trim() !== '' ? String(phone).trim() : null;

  if (verified) {
    return (
      <Tooltip title={phoneLabel ? `Verified · ${phoneLabel}` : 'Phone verified'}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0 : 0.75 }}>
          <Box
            sx={{
              width: compact ? 26 : 28,
              height: compact ? 26 : 28,
              borderRadius: '8px',
              bgcolor: '#F0FDF4',
              border: '1px solid #86efac',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: compact ? 15 : 17, color: '#16a34a' }} />
          </Box>
          {!compact && (
            <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.75rem', color: '#16a34a' }}>
              Verified
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={phoneLabel ? `Not verified · ${phoneLabel}` : 'Phone not verified'}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0 : 0.75 }}>
        <Box
          sx={{
            width: compact ? 26 : 28,
            height: compact ? 26 : 28,
            borderRadius: '8px',
            bgcolor: '#FFF7ED',
            border: `1px solid ${C.orange}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PhoneAndroidOutlinedIcon sx={{ fontSize: compact ? 14 : 15, color: C.orange }} />
        </Box>
        {!compact && (
          <>
            <HighlightOffRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
            <Typography sx={{ fontFamily: 'Inter Regular', fontSize: '0.75rem', color: C.textMuted }}>
              Pending
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
}

function EmptyTeamsState({ onAdd }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2.5,
        py: isMobile ? 8 : 12,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: isMobile ? 72 : 84,
          height: isMobile ? 72 : 84,
          borderRadius: '22px',
          background: `linear-gradient(135deg, ${C.orange}12 0%, ${C.orange}05 100%)`,
          border: `2px dashed ${C.orange}35`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <GroupsOutlinedIcon sx={{ fontSize: isMobile ? 34 : 40, color: `${C.orange}80` }} />
      </Box>
      <Box sx={{ textAlign: 'center', maxWidth: 320 }}>
        <Typography
          sx={{ fontFamily: 'Inter Bold', color: C.textPrimary, fontSize: isMobile ? '1rem' : '1.05rem', mb: 0.6 }}
        >
          No team members yet
        </Typography>
        <Typography
          sx={{ fontFamily: 'Inter Regular', color: C.textMuted, fontSize: '0.83rem', mb: 2.5 }}
        >
          Add your first team member to start managing roles and access for this branch.
        </Typography>
        <Button
          variant='contained'
          startIcon={<PersonAddAltOutlinedIcon />}
          onClick={onAdd}
          fullWidth={isMobile}
          sx={{
            background: `linear-gradient(135deg, ${C.orange} 0%, #ff7c47 100%)`,
            color: '#fff',
            fontFamily: 'Inter Bold',
            borderRadius: '11px',
            textTransform: 'none',
            px: 3,
            py: 1.1,
            fontSize: '0.85rem',
            boxShadow: C.shadowOrange,
            '&:hover': { background: `linear-gradient(135deg, #e84e10 0%, ${C.orange} 100%)` },
          }}
        >
          Add First Member
        </Button>
      </Box>
    </Box>
  );
}

/** Mobile: stacked member cards instead of table. */
function TeamsMobileList({
  teams,
  expandedRowKey,
  setExpandedRowKey,
  onEdit,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.5 }}>
      {teams.map((row, idx) => {
        const info = getTeamInfo(row);
        const rowKey = getTeamRowKey(row, idx);
        const expanded = expandedRowKey === rowKey;
        const fullName = [info.firstName, info.lastName].filter(Boolean).join(' ') || '—';

        return (
          <Paper
            key={rowKey}
            elevation={0}
            onClick={() => setExpandedRowKey(expanded ? null : rowKey)}
            sx={{
              border: `1px solid ${expanded ? `${C.orange}50` : C.border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              bgcolor: expanded ? C.rowExpanded : C.cardBg,
              boxShadow: expanded ? `0 0 0 1px ${C.orange}25` : 'none',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <Avatar
                  sx={avatarInitialSx({
                    width: 40,
                    height: 40,
                    fontSize: '0.82rem',
                    borderRadius: '10px',
                    flexShrink: 0,
                  })}
                >
                  {getInitials(info.firstName, info.lastName)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter Bold',
                      fontSize: '0.92rem',
                      color: C.textPrimary,
                      lineHeight: 1.2,
                    }}
                  >
                    {fullName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      color: C.textMuted,
                      mt: 0.35,
                      wordBreak: 'break-all',
                    }}
                  >
                    {formatCell(info.team_user_id)}
                  </Typography>
                </Box>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(row);
                  }}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    color: '#fff',
                    bgcolor: C.dark,
                    flexShrink: 0,
                    '&:hover': { bgcolor: C.orange },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1.25,
                  pt: 1.25,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <PhoneVerifiedCell
                  verified={isVerifiedStatus(info.phone_verified_status)}
                  phone={info.phone_no}
                  compact
                />
                <Chip
                  label={formatCell(info.user_type)}
                  size='small'
                  sx={{
                    fontFamily: 'Inter Bold',
                    fontSize: '0.65rem',
                    height: 22,
                    bgcolor: C.dark,
                    color: 'rgba(255,255,255,0.85)',
                    borderRadius: '6px',
                  }}
                />
                <AccountStatusChip status={info.account_status} reasonStatus={info.reason_status} />
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                  {expanded ? (
                    <KeyboardArrowUpIcon sx={{ fontSize: 20, color: C.orange }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: 20, color: C.textMuted }} />
                  )}
                </Box>
              </Box>
            </Box>

            <Collapse in={expanded} timeout='auto' unmountOnExit>
              <Box sx={{ borderTop: `1px solid ${C.border}`, bgcolor: C.headBg }}>
                <TeamRowDetailsPanel row={row} />
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}

const NOTICE_AUTO_DISMISS_MS = 6000;

/** Top-of-page notice — success (add/edit) or error (list load). */
function PageNotice({ notice, onClose }) {
  if (!notice?.message) return null;

  const isSuccess = notice.type === 'success';
  const styles = isSuccess
    ? { bg: '#f0fdf4', border: '#bbf7d0', iconColor: '#16a34a', textColor: '#14532d' }
    : { bg: '#fef2f2', border: '#fecaca', iconColor: '#dc2626', textColor: '#7f1d1d' };
  const Icon = isSuccess ? CheckCircleOutlineRoundedIcon : ErrorOutlineRoundedIcon;

  return (
    <Fade in timeout={280}>
      <Box
        role={isSuccess ? 'status' : 'alert'}
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.25,
          borderRadius: '10px',
          bgcolor: styles.bg,
          border: `1px solid ${styles.border}`,
          fontFamily: 'Inter Regular',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: styles.textColor,
        }}
      >
        <Icon sx={{ fontSize: 20, color: styles.iconColor, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: 'Inter Regular',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: styles.textColor,
            flex: 1,
            lineHeight: 1.5,
          }}
        >
          {notice.message}
        </Typography>
        <IconButton
          size='small'
          onClick={onClose}
          aria-label='Dismiss'
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            color: styles.iconColor,
            flexShrink: 0,
            '&:hover': { bgcolor: `${styles.border}80` },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Fade>
  );
}

/* ── stat pill (light hero) ── */
function StatPill({ icon: Icon, value, label, color }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: '10px',
        bgcolor: '#fff',
        border: `1px solid ${C.border}`,
        boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
      }}
    >
      <Icon sx={{ fontSize: 14, color }} />
      <Typography
        sx={{ fontFamily: 'Inter Bold', fontSize: '0.82rem', color: C.textPrimary, lineHeight: 1 }}
      >
        {value}
      </Typography>
      <Typography
        sx={{ fontFamily: 'Inter Regular', fontSize: '0.72rem', color: C.textMuted, lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function BranchTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Shown only after add/edit success — never from list API load */
  const [successNotice, setSuccessNotice] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  const loadTeams = useCallback(async () => {
    const branchName = resolveBranchNameForQuery();
    if (!branchName) {
      setTeams([]);
      setLoading(false);
      setError('No branch context found. Sign in again or ensure branch name is configured.');
      setSuccessNotice(null);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getTeamsByBranchAPI(branchName);
    if (res.hasError) {
      setTeams([]);
      setLoading(false);
      setError(
        typeof res.message === 'string' && res.message.trim()
          ? res.message.trim()
          : res.error?.message || 'Request failed'
      );
      return;
    }
    const body = res.data;
    if (body?.success === false) {
      setTeams([]);
      setLoading(false);
      setError(formatTeamsErrors(body.errors) || body.CustomMessage || 'Could not load teams.');
      return;
    }
    if (body?.success === true && body.data) {
      const list = Array.isArray(body.data.teams) ? body.data.teams : [];
      setTeams(list);
      setLoading(false);
      return;
    }
    setTeams([]);
    setLoading(false);
    setError('Unexpected response from server.');
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const branchName = resolveBranchNameForQuery();

  const activeCount = teams.filter((t) => isAccountActive(getTeamInfo(t))).length;
  const pendingCount = teams.filter((t) => isAccountPending(getTeamInfo(t))).length;

  const handleAddSuccess = () => {
    setSuccessNotice('Team member added successfully!');
    loadTeams();
  };
  const handleEditSuccess = (msg) => {
    setSuccessNotice(
      typeof msg === 'string' && msg.trim() ? msg.trim() : 'Team role access updated.'
    );
    loadTeams();
  };

  const dismissSuccessNotice = useCallback(() => setSuccessNotice(null), []);

  useEffect(() => {
    if (!successNotice) return undefined;
    const t = window.setTimeout(dismissSuccessNotice, NOTICE_AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [successNotice, dismissSuccessNotice]);

  const handleReload = () => {
    setSuccessNotice(null);
    loadTeams();
  };

  const pagePad = isMobile ? '12px 14px' : isIpad ? '20px 22px' : '28px 32px';
  const heroIconSize = isMobile ? 44 : isIpad ? 48 : 52;
  const phoneCompact = isIpad || isNarrowTable;
  const memberAvatarSize = isNarrowTable ? 32 : isIpad ? 34 : 38;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: C.pageBg, boxSizing: 'border-box' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          p: pagePad,
          boxSizing: 'border-box',
        }}
      >
        {/* ── TOP NOTICES (errors from list load; success from add/edit only) ── */}
        {error && (
          <PageNotice
            notice={{ type: 'error', message: error }}
            onClose={() => setError(null)}
          />
        )}
        {successNotice && (
          <PageNotice
            notice={{ type: 'success', message: successNotice }}
            onClose={dismissSuccessNotice}
          />
        )}

        {/* ═══════════════════════════════════════
            LIGHT HERO BANNER (Kitchen-adjacent: warm white + orange accent)
        ═══════════════════════════════════════ */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '5px',
            overflow: 'hidden',
            mb: 3,
            border: `2px solid rgb(24 20 54)`,
            background: '#ffffff',
          }}
        >
          {/* banner content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              px: isMobile ? 2 : isIpad ? 2.5 : 4,
              pt: isMobile ? 2 : isIpad ? 2.25 : 3,
              pb: isMobile ? 2 : isIpad ? 2.25 : 3.25,
              display: 'flex',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* left: icon + text */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, minWidth: 0 }}>
              <Box
                sx={{
                  width: heroIconSize,
                  height: heroIconSize,
                  borderRadius: '5px',
                  flexShrink: 0,
                  background: 'rgb(24, 20, 54)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GroupsOutlinedIcon sx={{ color: '#fff', fontSize: isMobile ? 22 : 26 }} />
              </Box>

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter Bold',
                      fontSize: isMobile ? '1.2rem' : isIpad ? '1.35rem' : '1.55rem',
                      color: C.textPrimary,
                      lineHeight: 1,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    Branch Teams
                  </Typography>
                  {branchName && (
                    <Box
                      sx={{
                        padding: '5px 10px 5px 10px',
                        borderRadius: '3px',
                        border: `1px solid ${C.orange}40`,
                        fontSize: '14px',
                        fontFamily: 'Inter Bold',
                        color: `${C.orange}`,
                        letterSpacing: '0.01em',
                        lineHeight: 1.4,
                      }}
                    >
                      {branchName}
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: '0.78rem', color: C.textMuted, fontFamily: 'Inter Regular' }}
                >
                  Live roster · click any row to expand access &amp; permissions
                </Typography>

                {/* inline stats */}
                {!loading && !error && teams.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <StatPill
                      icon={PeopleAltOutlinedIcon}
                      value={teams.length}
                      label='total'
                      color='#3F3F6E'
                    />
                    <StatPill
                      icon={VerifiedOutlinedIcon}
                      value={activeCount}
                      label='active'
                      color='#16a34a'
                    />
                    <StatPill
                      icon={HourglassEmptyOutlinedIcon}
                      value={pendingCount}
                      label='pending'
                      color={C.orange}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* right: actions */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-end',
              }}
            >
              <Tooltip title='Reload'>
                <span>
                  <IconButton
                    onClick={handleReload}
                    disabled={loading}
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '5px',
                      border: `1.5px solid ${C.border}`,
                      bgcolor: '#fff',
                      color: C.textSecondary,
                      '&:hover': { bgcolor: C.navy, borderColor: C.navy, color: '#fff' },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant='contained'
                startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setAddOpen(true)}
                fullWidth={isMobile}
                sx={{
                  background: 'rgb(24, 20, 54)',
                  color: '#fff',
                  fontFamily: 'Inter Regular',
                  fontSize: isMobile ? '0.8rem' : '0.84rem',
                  textTransform: 'none',
                  borderRadius: '5px',
                  px: 2.5,
                  height: 40,
                  flex: isMobile ? 1 : 'none',
                  letterSpacing: '0.01em',
                  '& .MuiButton-startIcon .MuiSvgIcon-root': { color: '#fff' },
                  '&:hover': { background: '#ff7c47' },
                }}
              >
                {isMobile ? 'Add' : 'Add Member'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ── LOADER ── */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 16,
              gap: 2.5,
              bgcolor: C.cardBg,
              borderRadius: '18px',
              border: `1px solid ${C.border}`,
              boxShadow: C.shadow,
            }}
          >
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: '18px',
                background: `linear-gradient(135deg, ${C.orange}18 0%, ${C.orange}08 100%)`,
                border: `1px solid ${C.orange}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={30} thickness={4} sx={{ color: C.orange }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  color: C.textPrimary,
                  fontFamily: 'Inter Bold',
                  fontSize: '0.96rem',
                  mb: 0.4,
                }}
              >
                Loading team members
              </Typography>
              <Typography
                sx={{ color: C.textMuted, fontFamily: 'Inter Regular', fontSize: '0.82rem' }}
              >
                Fetching the latest roster from your branch…
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ═══════════════════════════════════════
              TABLE CARD
          ═══════════════════════════════════════ */
          <Paper
            elevation={0}
            sx={{
              bgcolor: C.cardBg,
              border: `2px solid ${C.border}`,
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: C.shadow,
            }}
          >
            {teams.length === 0 ? (
              <EmptyTeamsState onAdd={() => setAddOpen(true)} />
            ) : isMobile ? (
              <TeamsMobileList
                teams={teams}
                expandedRowKey={expandedRowKey}
                setExpandedRowKey={setExpandedRowKey}
                onEdit={(row) => {
                  setSelectedMember(row);
                  setEditOpen(true);
                }}
              />
            ) : (
            <TableContainer
              sx={{
                width: '100%',
                overflowX: 'hidden',
              }}
            >
              <Table
                size='small'
                stickyHeader
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                }}
              >
                {/* DARK TABLE HEAD */}
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.member)}>Member</TableCell>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.teamId)}>Team User ID</TableCell>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.phone)}>Phone Verified</TableCell>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.userType)}>User Type</TableCell>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.status)}>Account Status</TableCell>
                    <TableCell sx={tableHeadCellSx(TABLE_COL_SX.actions)} />
                  </TableRow>
                </TableHead>

                <TableBody>
                    {teams.map((row, idx) => {
                      const info = getTeamInfo(row);
                      const rowKey = getTeamRowKey(row, idx);
                      const expanded = expandedRowKey === rowKey;
                      const fullName =
                        [info.firstName, info.lastName].filter(Boolean).join(' ') || '—';

                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            hover
                            onClick={() => setExpandedRowKey(expanded ? null : rowKey)}
                            sx={{
                              cursor: 'pointer',
                              position: 'relative',
                              bgcolor: expanded
                                ? C.rowExpanded
                                : idx % 2 === 0
                                  ? '#fff'
                                  : '#FAFBFC',
                              '&:hover': { bgcolor: C.rowHover },
                              '& > td': {
                                borderBottom: expanded ? 'none' : `1px solid ${C.border}`,
                              },
                              ...(expanded && {
                                '& > td:first-of-type': {
                                  boxShadow: `inset 3px 0 0 ${C.orange}`,
                                },
                              }),
                            }}
                          >
                            {/* member cell */}
                            <TableCell sx={{ ...TABLE_COL_SX.member, py: 1.75, pl: isNarrowTable ? 1 : 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: isNarrowTable ? 0.75 : 1.25,
                                  minWidth: 0,
                                }}
                              >
                                {/* expand chevron */}
                                <Box
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '6px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: expanded ? `${C.orange}14` : 'transparent',
                                    border: expanded
                                      ? `1px solid ${C.orange}35`
                                      : '1px solid transparent',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  {expanded ? (
                                    <KeyboardArrowUpIcon sx={{ fontSize: 15, color: C.orange }} />
                                  ) : (
                                    <KeyboardArrowDownIcon
                                      sx={{ fontSize: 15, color: C.textMuted }}
                                    />
                                  )}
                                </Box>

                                <Avatar
                                  sx={avatarInitialSx({
                                    width: memberAvatarSize,
                                    height: memberAvatarSize,
                                    fontSize: isNarrowTable ? '0.72rem' : '0.8rem',
                                    borderRadius: isNarrowTable ? '9px' : '11px',
                                    flexShrink: 0,
                                  })}
                                >
                                  {getInitials(info.firstName, info.lastName)}
                                </Avatar>

                                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                  <Tooltip title={fullName} disableHoverListener={fullName === '—'}>
                                    <Typography
                                      sx={{
                                        fontFamily: 'Inter Bold',
                                        fontSize: isNarrowTable ? '0.8rem' : '0.88rem',
                                        color: C.textPrimary,
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {fullName}
                                    </Typography>
                                  </Tooltip>
                                  {!isNarrowTable && info.dedicatedRole && (
                                    <Typography
                                      sx={{
                                        fontSize: '0.7rem',
                                        color: C.textMuted,
                                        fontFamily: 'Inter Regular',
                                        lineHeight: 1.3,
                                        textTransform: 'capitalize',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {String(info.dedicatedRole).replace('_', ' ')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell
                              sx={{
                                ...TABLE_COL_SX.teamId,
                                fontSize: '0.72rem',
                                color: C.textMuted,
                                fontFamily: 'monospace',
                              }}
                            >
                              <Tooltip
                                title={formatCell(info.team_user_id)}
                                disableHoverListener={!info.team_user_id}
                              >
                                <Box
                                  component='span'
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatCell(info.team_user_id)}
                                </Box>
                              </Tooltip>
                            </TableCell>

                            <TableCell sx={TABLE_COL_SX.phone}>
                              <PhoneVerifiedCell
                                verified={isVerifiedStatus(info.phone_verified_status)}
                                phone={info.phone_no}
                                compact={phoneCompact}
                              />
                            </TableCell>

                            <TableCell sx={TABLE_COL_SX.userType}>
                              <Chip
                                label={formatCell(info.user_type)}
                                size='small'
                                sx={{
                                  fontFamily: 'Inter Bold',
                                  fontSize: isNarrowTable ? '0.62rem' : '0.68rem',
                                  height: 22,
                                  maxWidth: '100%',
                                  bgcolor: C.dark,
                                  color: 'rgba(255,255,255,0.85)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell sx={TABLE_COL_SX.status}>
                              <AccountStatusChip
                                status={info.account_status}
                                reasonStatus={info.reason_status}
                              />
                            </TableCell>

                            {/* edit */}
                            <TableCell
                              onClick={(e) => e.stopPropagation()}
                              sx={{ ...TABLE_COL_SX.actions, pr: 1 }}
                            >
                              <Tooltip title='Edit role access' placement='left'>
                                <IconButton
                                  size='small'
                                  onClick={() => {
                                    setSelectedMember(row);
                                    setEditOpen(true);
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    color: '#fff',
                                    bgcolor: C.dark,
                                    border: `1px solid ${C.dark}`,
                                    boxShadow: '0 2px 8px rgba(15,23,42,0.22)',
                                    transition: 'all 0.18s',
                                    '&:hover': {
                                      bgcolor: C.orange,
                                      borderColor: C.orange,
                                      boxShadow: C.shadowOrange,
                                      transform: 'scale(1.08)',
                                    },
                                  }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>

                          {/* expandable detail row */}
                          <TableRow
                            sx={{
                              '& > td': {
                                py: 0,
                                borderBottom: expanded ? `1px solid ${C.border}` : 0,
                              },
                            }}
                          >
                            <TableCell colSpan={TABLE_COL_COUNT} sx={{ p: 0 }}>
                              <Collapse in={expanded} timeout='auto' unmountOnExit>
                                <TeamRowDetailsPanel row={row} />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {/* table footer */}
            {teams.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  px: isMobile ? 1.5 : isIpad ? 2.5 : 3,
                  py: 1.75,
                  bgcolor: C.headBg,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? '0.72rem' : '0.78rem',
                    color: C.textSecondary,
                    fontFamily: 'Inter Regular',
                  }}
                >
                  Showing{' '}
                  <Box component='span' sx={{ fontFamily: 'Inter Bold', color: C.textPrimary }}>
                    {teams.length}
                  </Box>{' '}
                  member{teams.length !== 1 ? 's' : ''}
                  {activeCount > 0 && (
                    <>
                      {' · '}
                      <Box component='span' sx={{ fontFamily: 'Inter Bold', color: '#16a34a' }}>
                        {activeCount}
                      </Box>
                      {' active'}
                    </>
                  )}
                </Typography>

                <Button
                  size='small'
                  startIcon={!isMobile ? <PersonAddAltOutlinedIcon sx={{ fontSize: 14 }} /> : null}
                  onClick={() => setAddOpen(true)}
                  sx={{
                    fontFamily: 'Inter Regular',
                    fontSize: isMobile ? '0.75rem' : '14px',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${C.dark} 0%, #1e293b 100%)`,
                    textTransform: 'none',
                    borderRadius: '5px',
                    px: isMobile ? 1.5 : 1.75,
                    py: 0.65,
                    minWidth: isMobile ? 'auto' : undefined,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${C.orange} 0%, #ff7c47 100%)`,
                    },
                  }}
                >
                  {isMobile ? 'Add' : 'Add Member'}
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* modals */}
        <AddMemberModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSuccess={handleAddSuccess}
          branchName={branchName}
        />
        <EditMemberModal
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setSelectedMember(null);
          }}
          onSuccess={handleEditSuccess}
          member={selectedMember}
          branchName={branchName}
        />
      </Box>
    </Box>
  );
}
